import { base64ToBlob } from "./file-bytes";
import { downloadBlob } from "./file-download";

const formats = [
  { kind: "word", mime: "application/vnd.openxmlformats-officedocument.wordprocessingml.document", extensions: ["docx"] },
  { kind: "pdf", mime: "application/pdf", extensions: ["pdf"] },
  { kind: "image", mime: "image/png", extensions: ["png"] },
  { kind: "image", mime: "image/jpeg", extensions: ["jpg", "jpeg"] },
  { kind: "image", mime: "image/webp", extensions: ["webp"] },
  { kind: "image", mime: "image/gif", extensions: ["gif"] },
  { kind: "native", mime: "text/plain", extensions: ["txt"] },
] as const;

type PreviewKind = typeof formats[number]["kind"];
export interface DocumentResource {
  readonly unavailableReason?: "original-missing" | "original-access";
  readonly name: string;
  readonly mimeType: string;
  readonly previewable: boolean;
  readonly previewKind: PreviewKind | null;
  readonly load: () => Promise<Blob>;
  readonly loadThumbnail?: () => Promise<string | null>;
}

export function documentPreviewCapability(input: { name: string; mimeType?: string | null }) {
  const mime = input.mimeType?.split(";", 1)[0]?.trim().toLowerCase() || "application/octet-stream";
  const extension = input.name.split(".").at(-1)?.toLowerCase();
  // An explicit unsupported type (especially HTML/SVG) must never become a preview via its filename.
  const format = formats.find(f => f.mime === mime) ?? (mime === "application/octet-stream"
    ? formats.find(f => (f.extensions as readonly string[]).includes(extension ?? "")) : undefined);
  const mimeType = format?.mime ?? mime;
  return { mimeType, previewable: Boolean(format), previewKind: format?.kind ?? null };
}

export function createDocumentResource(input: { name: string; mimeType?: string | null; load: () => Promise<Blob>; loadThumbnail?: () => Promise<string | null> }): DocumentResource {
  return Object.freeze({ ...input, ...documentPreviewCapability(input) });
}

export function createUnavailableDocumentResource(input: { name: string; mimeType?: string | null; reason: NonNullable<DocumentResource["unavailableReason"]> }): DocumentResource {
  return Object.freeze({ ...documentPreviewCapability(input), name: input.name, previewable: false, previewKind: null,
    unavailableReason: input.reason, load: async () => { throw new Error("Original file unavailable"); } });
}

export interface PreparedDocumentPreview {
  blob: Blob;
  html?: string;
  excerpt?: string;
  pdf?: boolean;
  url?: string;
  thumbnail?: string;
  dispose: () => void;
}

// Unlabeled text falls back to the locale's legacy encoding (windows-1252 for de/en); label only valid UTF-8 so
// Latin-1 files keep that fallback.
async function textMimeType(blob: Blob, mimeType: string): Promise<string> {
  const bytes = await blob.arrayBuffer();
  try {
    new TextDecoder("utf-8", { fatal: true }).decode(bytes);
  } catch {
    return mimeType;
  }
  return `${mimeType};charset=utf-8`;
}

export async function prepareDocumentPreview(resource: DocumentResource): Promise<PreparedDocumentPreview> {
  const original = await resource.load();
  const actual = createDocumentResource({ name: resource.name, mimeType: original.type && original.type !== "application/octet-stream" ? original.type : resource.mimeType, load: resource.load });
  const type = actual.previewKind === "native" ? await textMimeType(original, actual.mimeType) : actual.mimeType;
  const blob = original.type === type ? original : original.slice(0, original.size, type);
  if (!actual.previewable) return { blob, dispose: () => {} };
  if (actual.previewKind === "pdf") return { blob, pdf: true, dispose: () => {} };
  if (actual.previewKind === "word") {
    const { renderWordPreview } = await import("./word-preview");
    return { blob, ...await renderWordPreview(blob), dispose: () => {} };
  }
  const url = URL.createObjectURL(blob);
  return { blob, url, thumbnail: actual.previewKind === "image" ? url : undefined, dispose: () => URL.revokeObjectURL(url) };
}

export async function downloadDocument(resource: DocumentResource): Promise<void> {
  downloadBlob(await resource.load(), resource.name);
}

/** Prefer the authorized server rendition; generate only when none is available. */
export async function prepareDocumentThumbnail(resource: DocumentResource): Promise<PreparedDocumentPreview> {
  if (resource.loadThumbnail) {
    const thumbnail = await resource.loadThumbnail().catch(() => null);
    if (thumbnail?.match(/^data:image\/(png|jpeg|webp|gif);base64,/)) {
      return { blob: new Blob(), thumbnail, dispose: () => {} };
    }
  }
  const preview = await prepareDocumentPreview(resource);
  if (preview.pdf) {
    const { renderPdfThumbnail } = await import("./pdf-thumbnail");
    return { ...preview, thumbnail: await renderPdfThumbnail(preview.blob) };
  }
  if (resource.previewKind === "native" && preview.url) return { ...preview, excerpt: (await preview.blob.text()).slice(0, 2000) };
  return preview;
}

export type InMemoryDocument = File | { name: string; mimeType: string; content: string };
export function createInMemoryDocumentResource(file: InMemoryDocument): DocumentResource {
  return createDocumentResource({ name: file.name, mimeType: "content" in file ? file.mimeType : file.type,
    load: async () => "content" in file ? base64ToBlob(file.content, file.mimeType) : file });
}
